// @systeme: contrat
// L'espace d'administration (DEC-011) : des réglages, des plans, des prompts, des
// interrupteurs, des chiffres, les retours, les appels, le journal, la marge par foyer,
// et l'accès support (à la demande, limité, consigné). Jamais un contenu d'utilisatrice
// hors d'un accès support ouvert.
import { z } from 'zod';
import { SchemaClePlan, SchemaDroitPlan, SchemaFournisseurLlm, SchemaTypeQuota, } from '../enumerations.js';
import { SchemaInstant } from './elements.js';
import { SchemaRetour } from './retours.js';
import { SchemaStatutAbonnement } from '../enumerations.js';
export const SchemaReglageAdmin = z.object({
    cle: z.string(),
    valeur: z.unknown(),
    description: z.string(),
    majLe: SchemaInstant.nullable(),
});
export const SchemaMajReglage = z.object({ valeur: z.unknown() });
export const SchemaPlanAdmin = z.object({
    cle: SchemaClePlan,
    nom: z.string(),
    description: z.string(),
    prixTtc: z.number().min(0),
    droits: z.record(SchemaDroitPlan, z.boolean()),
    quotas: z.record(SchemaTypeQuota, z.number().int().min(0)),
    actif: z.boolean(),
    /** Vrai si un prix existe chez Stripe pour ce tarif. */
    stripePret: z.boolean(),
    /** Les abonnées actives qui paient encore un ancien tarif. */
    abonneesAncienTarif: z.number().int(),
});
export const SchemaMajPlan = z
    .object({
    nom: z.string().trim().min(1).max(60),
    description: z.string().trim().max(200),
    prixTtc: z.number().min(0).max(999),
    /** Champ par champ : ce qui n'est pas cité ne bouge pas. */
    droits: z.partialRecord(SchemaDroitPlan, z.boolean()),
    quotas: z.partialRecord(SchemaTypeQuota, z.number().int().min(0).max(100000)),
    actif: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { error: 'Rien à modifier.' });
export const SchemaPromptAdmin = z.object({
    id: z.uuid(),
    cle: z.string(),
    version: z.number().int(),
    contenu: z.string(),
    note: z.string().nullable(),
    actif: z.boolean(),
    creeLe: SchemaInstant,
});
export const SchemaCreationVersionPrompt = z.object({
    cle: z.string().min(1).max(60),
    contenu: z.string().trim().min(20).max(20000),
    note: z.string().trim().max(500).optional(),
});
export const SchemaLancementBanc = z.object({
    cle: z.string().min(1),
    version: z.number().int().min(1),
    modele: z.string().max(80).optional(),
    fournisseur: SchemaFournisseurLlm.optional(),
});
export const SchemaResultatBanc = z.object({
    reussis: z.number().int(),
    total: z.number().int(),
    latenceMs: z.number(),
    cout: z.number(),
    cas: z.array(z.object({ cle: z.string(), reussi: z.boolean(), raisons: z.array(z.string()) })),
});
export const SchemaBancEssai = z.object({
    id: z.uuid(),
    promptCle: z.string(),
    promptVersion: z.number().int(),
    modele: z.string().nullable(),
    fournisseur: z.string().nullable(),
    statut: z.enum(['en_cours', 'termine', 'erreur']),
    resultat: SchemaResultatBanc.nullable(),
    erreur: z.string().nullable(),
    creeLe: SchemaInstant,
    termineLe: SchemaInstant.nullable(),
});
/** Un retour vu de l'admin : le message (c'est son objet), le foyer, et le traitement. */
export const SchemaRetourAdmin = SchemaRetour.extend({
    espaceId: z.uuid(),
    traiteLe: SchemaInstant.nullable(),
    reponse: z.string().nullable(),
});
export const SchemaTraitementRetour = z.object({ reponse: z.string().trim().max(2000).optional() });
export const SchemaStatsAppels = z.object({
    jours: z.array(z.object({
        jour: z.string(),
        appels: z.number().int(),
        erreurs: z.number().int(),
        cout: z.number(),
        dureeMoyenneMs: z.number(),
    })),
    trenteJours: z.object({
        appels: z.number().int(),
        erreurs: z.number().int(),
        cout: z.number(),
        escalades: z.number().int(),
    }),
});
export const SchemaEntreeAudit = z.object({
    id: z.uuid(),
    utilisateurId: z.uuid().nullable(),
    espaceId: z.uuid().nullable(),
    action: z.string(),
    cibleType: z.string().nullable(),
    cibleId: z.uuid().nullable(),
    details: z.unknown(),
    creeLe: SchemaInstant,
});
export const SchemaTableauDeBord = z.object({
    foyers: z.number().int(),
    utilisatrices: z.number().int(),
    suppressionsEnAttente: z.number().int(),
    bancsEnCours: z.number().int(),
    abonnements: z.record(z.string(), z.number().int()),
    capturesTrenteJours: z.number().int(),
    retoursNonTraites: z.number().int(),
    accesSupportOuverts: z.number().int(),
    /** Payantes rapportées à celles dont l'essai est fini (payantes + expirées). */
    tauxConversion: z.number().nullable(),
    resiliationsTrenteJours: z.number().int(),
    /** Pour les abonnements arrêtés : durée moyenne entre la souscription et la fin. */
    dureeVieMoyenneJours: z.number().nullable(),
    foyersDeficitaires: z.number().int(),
    /** L'IA aujourd'hui, tous foyers, et le plafond réglé (0 = aucun). */
    depenseIaAujourdhui: z.number(),
    plafondIaJour: z.number(),
});
/** Un foyer vu de l'admin : usage, coût de l'IA, marge. Aucun contenu. */
export const SchemaFoyerAdmin = z.object({
    id: z.uuid(),
    nom: z.string(),
    creeLe: SchemaInstant,
    membres: z.number().int(),
    planCle: SchemaClePlan,
    statut: SchemaStatutAbonnement,
    prixTtc: z.number().nullable(),
    essaiFinLe: z.iso.date().nullable(),
    vocauxMois: z.number().int(),
    photosMois: z.number().int(),
    coutIaTrenteJours: z.number(),
    /** Vrai quand le coût de l'IA sur trente jours dépasse le prix payé. */
    deficitaire: z.boolean(),
});
export const SchemaProlongationEssai = z.object({ essaiFinLe: z.iso.date() });
export const SchemaAccesSupport = z.object({
    id: z.uuid(),
    espaceId: z.uuid(),
    nomEspace: z.string(),
    motif: z.string(),
    ouvertPar: z.uuid(),
    accordeLe: SchemaInstant,
    expireLe: SchemaInstant,
    revoqueLe: SchemaInstant.nullable(),
    ouvert: z.boolean(),
});
export const SchemaOuvertureSupport = z.object({
    espaceId: z.uuid(),
    /** La demande écrite de l'utilisatrice, en une phrase : où, quand, quoi. */
    motif: z.string().trim().min(10).max(500),
    dureeHeures: z.number().int().min(1).max(168).default(24),
});
/** Ce que l'admin voit d'un foyer pendant un accès support : ni privé, ni santé. */
export const SchemaVueSupport = z.object({
    espace: z.object({ id: z.uuid(), nom: z.string() }),
    acces: SchemaAccesSupport,
    personnes: z.array(z.object({ prenom: z.string(), role: z.string() })),
    elements: z.array(z.object({
        id: z.uuid(),
        type: z.string(),
        titre: z.string(),
        statut: z.string(),
        origine: z.string(),
        debutLe: SchemaInstant.nullable(),
        creeLe: SchemaInstant,
    })),
    captures: z.array(z.object({
        id: z.uuid(),
        statut: z.string(),
        erreur: z.string().nullable(),
        creeLe: SchemaInstant,
    })),
});
/** La double authentification de l'admin. */
export const SchemaEtatDeuxFacteurs = z.object({
    active: z.boolean(),
    /** Vrai quand la session courante a présenté un code récent. */
    verifiee: z.boolean(),
    /** Seulement tant que rien n'est confirmé : à saisir dans l'application d'authentification. */
    secret: z.string().optional(),
    url: z.string().optional(),
});
export const SchemaCodeTotp = z.object({ code: z.string().regex(/^\d{6}$/) });
//# sourceMappingURL=admin.js.map